import React, { useCallback, useEffect, useMemo } from 'react';
import { Box, Stack, Tab, Typography } from '@mui/material';
import type { PageEntityConfig } from 'c-config';
import { PostAuthRoutes } from 'c-routes';
import type { DisplayMediaBreakdownData, Displays_Screen } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import { useEntityPage, useUrlState, useUrlStateValue } from 'c-hooks';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import { useCommonTranslation } from 'c-translation';
import { useForm } from 'react-hook-form';

import { useUpdateEntityData } from 'c-data';
import { FilterableEntityTable, FilterableEntityTableRendererType } from 'c-pagination';
import {
    displayEditPacksColumns,
    displayEditTagColumns,
} from 'c-displays/Components/DisplaysEntityColumns';
import { FullPageEntityTableWrapper } from 'c-wrapper';
import { VendorIDs } from 'c-displays/Components/VendorIDs';
import ReactGA from 'react-ga';
import { removeEmptyProperties } from 'c-pagination/Components/EntityFilters/utils';
import { generatePath } from 'react-router-dom';

import Audience from 'c-displays/Pages/DisplaysDisplayEdit/Audience';
import ChannelFrame from 'c-displays/Pages/DisplaysDisplayEdit/ChannelFrame';
import ApiClient from 'c-data/apiClient';
import type { ReportConfig } from 'c-reports/Types';
import { ReportDateTimeframe } from 'c-reports/Types';
import { LogFileParsedDaily } from 'c-pop-displays/Components/LogFileParsed';
import { parseISO, format, isValid } from 'date-fns';
import { DisplayForm } from './DisplayForm';
import { OperationalTimings } from '../../Components/OperationalTimings';

const textSearchCols = ['id'];

export const toDateOnly = (dateString?: string | null): string | null => {
    if (!dateString) return null;
    const date = parseISO(dateString); // Safely parses ISO 8601 strings
    return isValid(date) ? format(date, 'yyyy-MM-dd') : null;
};

const DisplaysDisplayEditPage = () => {
    useEffect(() => {
        ReactGA.pageview(window.location.pathname + window.location.search);
    }, []);
    const t = useCommonTranslation();
    const { entity, id } = useEntityPage<Displays_Screen>();
    const { push } = useUrlState();
    const urlTab = useUrlStateValue<string>('tab', 'display');

    const reportMinDate = useMemo(
        () =>
            entity?.channelFrameSummary?.first_seen
                ? new Date(entity?.channelFrameSummary?.first_seen)
                : parseISO('1990-01-01'),
        [entity?.channelFrameSummary?.first_seen],
    );
    const reportMaxDate = useMemo(
        () =>
            entity?.channelFrameSummary?.last_seen
                ? new Date(entity?.channelFrameSummary?.last_seen)
                : new Date(),
        [entity?.channelFrameSummary?.last_seen],
    );

    const fetchDailyData: ReportConfig<{ data: DisplayMediaBreakdownData[] }>['fetchData'] =
        useCallback(
            (rangeStart, rangeEnd, timeframe, group_by) => {
                const params: any = {};

                if (timeframe === ReportDateTimeframe.All) {
                    if (reportMinDate)
                        params.start_date = `${format(reportMinDate, 'yyyy-MM-dd')} 00:00:00`;
                    if (reportMaxDate)
                        params.end_date = `${format(reportMaxDate, 'yyyy-MM-dd')} 23:59:59`;
                } else {
                    if (rangeStart)
                        params.start_date = `${format(rangeStart, 'yyyy-MM-dd')} 00:00:00`;
                    if (rangeEnd) params.end_date = `${format(rangeEnd, 'yyyy-MM-dd')} 23:59:59`;
                }

                if (group_by?.length) params.group_by = group_by;

                return ApiClient.Entities.Displays_Screen.displayMediaBreakdown(id, params);
            },
            [id, reportMaxDate, reportMinDate],
        );

    const paginationTagForTags = `displays-displayEdit-admin-Tags-list-${id}`;
    const paginationTagForPacks = `displays-displayEdit-admin-Packs-list-${id}`;

    const { update, getUpdatingById } = useUpdateEntityData('Displays_Screen');
    const { state, error } = useMemo(() => getUpdatingById(id), [getUpdatingById, id]);
    const defaultValues = useMemo(
        () => ({
            ...entity,
            market_id: entity?.market_id || '',
            partner_id: entity?.partner_id || '',
            owner_id: entity?.owner?.id || '',
            filetypes: entity?.fileTypes.map(file => file.type) ?? [],
        }),
        [entity],
    );
    // const { start, data } = useAPIClientRequest(ApiClient.Entities.Displays_AudienceUpload.list);
    // useEffect(() => {
    //     start({
    //         // includes: ['audienceSource'],
    //         filters: {
    //             'filter.display_id': '1234569931',
    //             'filter.audience_version_id': 1,
    //             'filter.valid_from': ['>=2021-09-01'],
    //         },
    //     });
    // }, [start, id]);
    const handleFormSubmit = useCallback(
        data => {
            const dataWithNewChanges = removeEmptyProperties(data);
            delete dataWithNewChanges.fileTypes;
            delete dataWithNewChanges.mediaOwners;
            delete dataWithNewChanges.owner;
            update(id, dataWithNewChanges as any);
        },
        [id, update],
    );

    const {
        handleSubmit,
        control,
        getValues,
        setValue: setFormValue,
        watch,
    } = useForm({
        defaultValues,
    });

    const operationalTimingsDisplay = useMemo(() => {
        const operationalTimes = entity.operationalTimes;
        return (
            <OperationalTimings
                operationalTimings={operationalTimes}
                id={id}
                entityName="Displays_Screen"
            />
        );
    }, [entity.operationalTimes, id]);

    const VendorIDsDisplay = useMemo(() => {
        const vendorIds = entity.displaysVendorIds;
        return <VendorIDs id={id} vendorIds={vendorIds} entity={entity} />;
    }, [entity, id]);

    const operationalTabContent = useMemo<React.ReactNode>(
        () => operationalTimingsDisplay,
        [operationalTimingsDisplay],
    );

    const tagsTabContent = useMemo<React.ReactNode>(
        () => (
            <>
                {entity.tags.length > 0 && (
                    <FullPageEntityTableWrapper title="Modules.Displays.DisplaysDisplayEdit.tabs.tagTab">
                        <FilterableEntityTable
                            key={paginationTagForTags}
                            tag={paginationTagForTags}
                            columns={displayEditTagColumns}
                            onRowClick={id => {
                                const rout = generatePath(PostAuthRoutes.Displays.DisplaysTagEdit, {
                                    id,
                                });
                                window.open(rout, '_blank');
                            }}
                            baseEntityName="Displays_Tag"
                            textSearchColumns={textSearchCols}
                            rendererType={FilterableEntityTableRendererType.Contained}
                            routeTemplate={PostAuthRoutes.Displays.DisplaysTagEdit}
                            direction="desc"
                            filters={{
                                'filter.id': entity.tags.map(tag => tag.id),
                            }}
                            showFilters
                            includes={[
                                'screensCount',
                                'operationalTimeScreensCount',
                                'activeScreensCount',
                                'inactiveScreensCount',
                                'tags',
                            ]}
                        />
                    </FullPageEntityTableWrapper>
                )}
                {entity.tags.length === 0 && (
                    <Typography>
                        {t('Modules.Displays.DisplaysDisplayEdit.tabs.emptyTagTab')}{' '}
                    </Typography>
                )}
            </>
        ),
        [entity.tags, paginationTagForTags, t],
    );

    const packsTabContent = useMemo<React.ReactNode>(
        () => (
            <>
                {entity.packs.length > 0 && (
                    <FullPageEntityTableWrapper title="Modules.Displays.DisplaysDisplayEdit.tabs.packsTab">
                        <FilterableEntityTable
                            key={paginationTagForPacks}
                            tag={paginationTagForPacks}
                            columns={displayEditPacksColumns}
                            onRowClick={id => {
                                const rout = generatePath(
                                    PostAuthRoutes.Displays.DisplaysPackEdit,
                                    { id },
                                );
                                window.open(rout, '_blank');
                            }}
                            baseEntityName="Displays_Pack"
                            textSearchColumns={textSearchCols}
                            rendererType={FilterableEntityTableRendererType.Contained}
                            direction="desc"
                            showFilters
                            filters={{
                                'filter.id': entity.packs.map(pack => pack.id),
                            }}
                            includes={[
                                'country',
                                'activeDisplaysCount',
                                'inactiveDisplaysCount',
                                'resolutions',
                                'market',
                            ]}
                        />
                    </FullPageEntityTableWrapper>
                )}
                {entity.packs.length === 0 && (
                    <Typography>
                        {t('Modules.Displays.DisplaysDisplayEdit.tabs.emptyPacksTab')}
                    </Typography>
                )}
            </>
        ),
        [entity.packs, paginationTagForPacks, t],
    );

    const vendorTabContent = useMemo<React.ReactNode>(() => VendorIDsDisplay, [VendorIDsDisplay]);

    const audienceTabContent = useMemo<React.ReactNode>(
        () => <Audience audienceVersions={entity?.audienceVersion} frameId={entity.frame_id} />,
        [entity?.audienceVersion, entity.frame_id],
    );

    const channelFramesTabContent = useMemo<React.ReactNode>(
        () => <ChannelFrame data={(entity as any).channelFrame} id={entity.id} />,
        [entity],
    );

    const dateBreakdownTabContent = useMemo<React.ReactNode>(
        () => (
            <Box sx={{ backgroundColor: 'white' }}>
                <LogFileParsedDaily
                    minDate={reportMinDate}
                    maxDate={reportMaxDate}
                    chartName={`${id}-daily-totals`}
                    fetchData={fetchDailyData}
                    defaultGroupByMedia
                    urlStatePath="display.dateBreakdown"
                    firstAndLastSeen={
                        entity?.channelFrameSummary?.last_seen &&
                        entity?.channelFrameSummary?.first_seen
                            ? {
                                  firstSeen: toDateOnly(entity.channelFrameSummary.first_seen),
                                  lastSeen: toDateOnly(entity.channelFrameSummary.last_seen),
                              }
                            : null
                    }
                />
            </Box>
        ),
        [entity?.channelFrameSummary, fetchDailyData, id, reportMaxDate, reportMinDate],
    );

    return (
        <Box sx={{ width: '100%' }}>
            <TabContext value={urlTab}>
                <Box pb={2}>
                    <TabList
                        onChange={(_, nextValue: string) => push('tab', nextValue)}
                        scrollButtons="auto"
                        centered
                        variant="fullWidth"
                    >
                        <Tab
                            value="display"
                            label={t('Modules.Displays.DisplaysDisplayEdit.tabs.displayTab')}
                        />
                        <Tab
                            value="operational"
                            label={t(
                                'Modules.Displays.DisplaysDisplayEdit.tabs.operationalTimingsTab',
                            )}
                        />
                        <Tab
                            value="tags"
                            label={t('Modules.Displays.DisplaysDisplayEdit.tabs.tagTab')}
                        />
                        <Tab
                            value="packs"
                            label={t('Modules.Displays.DisplaysDisplayEdit.tabs.packsTab')}
                        />
                        <Tab
                            value="vendor"
                            label={t('Modules.Displays.DisplaysDisplayEdit.tabs.vendorTab')}
                        />
                        <Tab
                            value="audience"
                            label={t('Modules.Displays.DisplaysDisplayEdit.tabs.audience')}
                        />
                        <Tab
                            value="channel-frames"
                            label={t('Modules.Displays.DisplaysDisplayEdit.tabs.channelFramesTab')}
                        />
                        <Tab
                            value="date-breakdown"
                            label={t('Modules.Displays.DisplaysDisplayEdit.tabs.dateBreakdownTab')}
                        />
                    </TabList>
                </Box>
                <TabPanel value="display" sx={{ p: 0 }}>
                    <Box>
                        <Stack direction="row" pb={2} gap={4} alignItems="center">
                            <Typography variant="h1">
                                {t('Modules.Displays.DisplaysDisplayEdit.description')}
                            </Typography>
                            <Typography variant="h4">{entity.address}</Typography>
                        </Stack>
                        <form onSubmit={handleSubmit(handleFormSubmit)}>
                            <DisplayForm
                                control={control}
                                state={state}
                                error={error}
                                setFormValue={setFormValue}
                                id={id}
                                getValues={getValues}
                                entity={entity}
                                watch={watch}
                            />
                        </form>
                    </Box>
                </TabPanel>
                <TabPanel value="operational" sx={{ p: 0 }}>
                    {operationalTabContent}
                </TabPanel>
                <TabPanel value="tags" sx={{ p: 0 }}>
                    {tagsTabContent}
                </TabPanel>
                <TabPanel value="packs" sx={{ p: 0 }}>
                    {packsTabContent}
                </TabPanel>
                <TabPanel value="vendor" sx={{ p: 0 }}>
                    {vendorTabContent}
                </TabPanel>
                <TabPanel value="audience" sx={{ p: 0 }}>
                    {audienceTabContent}
                </TabPanel>
                <TabPanel value="channel-frames" sx={{ p: 0 }}>
                    {channelFramesTabContent}
                </TabPanel>
                <TabPanel value="date-breakdown" sx={{ p: 0 }}>
                    {dateBreakdownTabContent}
                </TabPanel>
            </TabContext>
        </Box>
    );
};

const setup: PageEntityConfig<Displays_Screen> = {
    id: 'DisplaysDisplayEditPage',
    component: DisplaysDisplayEditPage,
    systemSearch: {
        title: 'Modules.Displays.DisplaysDisplayEdit.metaTitle',
        description: 'Modules.Displays.DisplaysDisplayEdit.description',
        icon: undefined,
        type: 'entity',
        aliases: undefined,
        route: PostAuthRoutes.Displays.DisplaysDisplayEdit,
        saveAsRecentPage: true,
        breadcrumbs: [
            { label: 'Modules.Admin.Home.metaTitle', link: PostAuthRoutes.Admin.AdminHome },
            {
                label: 'Modules.Displays.AdminHome.metaTitle',
                link: PostAuthRoutes.Displays.DisplaysAdminHome,
            },
            {
                label: 'Modules.Displays.DisplaysList.metaTitle',
                link: PostAuthRoutes.Displays.DisplaysList,
            },
        ],
        entityName: 'Displays_Screen',
        nameGen: (entity, id) => String(id),
        permissions: [PermissionName.Admin],
        defaultIncludes: [
            'energy_conversion_override',
            'energy_uses_per_second',
            'fileTypes',
            'tags',
            'operationalTimes',
            'packs',
            'markets',
            'resolutions',
            'displaysVendorIds',
            'owner',
            'audienceVersion.source',
            'channelFrame',
            'channelFrameSummary',
        ],
    },
};
export default setup;
